import { GetBestContentsQuery } from '@diritto/common/api/RelayContentApi';
import { GetSearchQuery } from '@diritto/common/api/SearchApi';
import { BestType } from '@diritto/common/best/constants';
import { EpisodeOrderType } from '@diritto/common/episode/constants';
import { SectionPosition } from '@diritto/common/home/constants/section-constants';
import { SectionContentRelayRequestDto, SectionRelayRequestDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { QueryKey } from 'constants/reactQuery';

export const relayQueryKey = {
  relayHomeSections: [QueryKey.Relay, QueryKey.HomeSection],
  relayHomeSectionsPersonal: [QueryKey.Relay, QueryKey.HomeSection, QueryKey.Personal],
  relayContent: [QueryKey.Relay, QueryKey.Content],
  relayContents: [QueryKey.Relay, QueryKey.Content, 'list'],
  relaySection: [QueryKey.Relay, QueryKey.Section],
  relayContentSearch: [QueryKey.Relay, QueryKey.Content, QueryKey.Search],
  relayEpisode: [QueryKey.Relay, QueryKey.Episode],
  relayProfile: [QueryKey.Relay, QueryKey.Profile],
  relayFancyShare: [QueryKey.Relay, QueryKey.FancyShare],
  relayRolling: [QueryKey.Relay, QueryKey.Rolling],
  relayProfileLite: [QueryKey.Relay, QueryKey.Profile, 'lite'],
  relayBests: [QueryKey.Relay, QueryKey.Best] as const,

  sections: ({ categoryIds, ...query }: SectionRelayRequestDto) =>
    [...relayQueryKey.relayHomeSections, query, categoryIds?.sort()] as const,
  sectionsPersonal: (includeAdult: boolean, position: SectionPosition) =>
    [...relayQueryKey.relayHomeSectionsPersonal, includeAdult, position] as const,
  sectionContent: (slug: string, data: SectionContentRelayRequestDto) =>
    [...relayQueryKey.relaySection, slug, data] as const,

  content: (contentId: number) => [...relayQueryKey.relayContent, contentId] as const,
  contentList: ({ contentIds, includeAdult }: { contentIds: number[]; includeAdult: boolean }) =>
    [...relayQueryKey.relayContents, includeAdult, contentIds.join('|')] as const,

  contentEpisodeList: (contentId: number, orderType: EpisodeOrderType) =>
    [...relayQueryKey.content(contentId), ...relayQueryKey.relayEpisode, orderType] as const,
  contentNoticeList: (contentId: number, orderType: EpisodeOrderType) =>
    [...relayQueryKey.content(contentId), ...relayQueryKey.relayEpisode, 'notice', orderType] as const,
  episodeList: (episodeIds: number[]) => [...relayQueryKey.relayEpisode, episodeIds.join('|')] as const,
  episodeView: (episodeId: number) => [...relayQueryKey.relayEpisode, 'view', episodeId] as const,

  fancyShare: (fancyShareId: number) => [...relayQueryKey.relayFancyShare, fancyShareId] as const,

  rollingById: (rollingId: number, includeAdult: boolean) =>
    [...relayQueryKey.relayRolling, 'rolling', rollingId, includeAdult] as const,
  rollingsByIds: ({ rollingIds, includeAdult }: { rollingIds: number[]; includeAdult: boolean }) =>
    [...relayQueryKey.relayRolling, 'rollings', includeAdult, rollingIds.join('|')] as const,
  randomRollings: (params: { tag?: string; includeAdult: boolean }) =>
    [...relayQueryKey.relayRolling, 'random', params.tag, params.includeAdult] as const,
  profileRollings: (profileId: number, includeAdult: boolean) =>
    [...relayQueryKey.relayRolling, 'profile', profileId, includeAdult] as const,
  contentRollings: (contentId: number, includeAdult: boolean) =>
    [...relayQueryKey.relayRolling, 'content', contentId, includeAdult] as const,

  profileList: (profileIds: number[]) => [...relayQueryKey.relayProfile, profileIds.join('|')] as const,
  profileLiteList: (profileIds: number[]) => [...relayQueryKey.relayProfileLite, profileIds.join('|')] as const,

  searchList: (keyword: string, options: GetSearchQuery) =>
    [...relayQueryKey.relayContentSearch, keyword, options] as const,

  bestList: (bestType: BestType, options: GetBestContentsQuery) => [...relayQueryKey.relayBests, bestType, options],
};
